import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Toaster } from './components/ui/sonner';
import Layout from './components/Layout';
import HomePage from './pages/HomePage';
import ContactPage from './pages/ContactPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import LoginPage from './pages/LoginPage';
import PasswordResetPage from './pages/PasswordResetPage';
import MemberHubPage from './pages/MemberHubPage';
import DashboardPage from './pages/DashboardPage';
import MembersPageNew from './pages/MembersPageNew';
import MemberDetailPage from './pages/MemberDetailPage';
import MemberProfilePage from './pages/MemberProfilePage';
import PortalMembersDirectory from './pages/PortalMembersDirectory';
import AttendancePage from './pages/AttendancePage';
import CalendarPage from './pages/CalendarPage';
import AppCalendarPage from './pages/AppCalendarPage';
import AdminCalendarPage from './pages/AdminCalendarPage';
import LogbookPage from './pages/LogbookPage';
import ManageActivitiesPage from './pages/ManageActivitiesPage';
import PermissionEditorPage from './pages/PermissionEditorPage';
import TimecardPage from './pages/TimecardPage';
import DutyOfficerTimecardPage from './pages/DutyOfficerTimecardPage';
import AuditLogPage from './pages/AuditLogPage';
import SecurityDashboardPage from './pages/SecurityDashboardPage';
import ContactSubmissionsPage from './pages/ContactSubmissionsPage';
import EquipmentPage from './pages/EquipmentPage';
import VehiclesPage from './pages/VehiclesPage';
import FormsPage from './pages/FormsPage';
import SettingsPage from './pages/SettingsPage';
import ResourcesPage from './pages/ResourcesPage';
import TrainingPage from './pages/TrainingPage';
import LinksPage from './pages/LinksPage';
import { isNative, initializeNativeApp, setupBackButtonHandler } from './utils/native';
import './App.css';

const ProtectedRoute = ({ children }) => {
    const { isAuthenticated, loading, requiresPasswordReset, completePasswordReset } = useAuth();
    
    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background">
                <div className="flex flex-col items-center gap-4">
                    <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
                    <p className="text-muted-foreground">Loading...</p>
                </div>
            </div>
        );
    }
    
    if (!isAuthenticated) {
        return <Navigate to="/login" replace />;
    }
    
    if (requiresPasswordReset) {
        return <PasswordResetPage onResetComplete={completePasswordReset} />;
    }
    
    return children;
};

const AppRoutes = () => {
    const { isAuthenticated, loading, requiresPasswordReset, completePasswordReset } = useAuth();
    
    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background">
                <div className="flex flex-col items-center gap-4">
                    <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
                    <p className="text-muted-foreground">Loading...</p>
                </div>
            </div>
        );
    }
    
    // If authenticated but requires password reset, show reset page
    if (isAuthenticated && requiresPasswordReset) {
        return <PasswordResetPage onResetComplete={completePasswordReset} />;
    }
    
    return (
        <Routes>
            {/* Public Marketing Homepage - Skip for native apps */}
            <Route path="/" element={
                isAuthenticated ? <Navigate to="/portal" replace /> : 
                isNative ? <Navigate to="/login" replace /> : <HomePage />
            } />
            
            {/* Public Contact Page - Skip for native apps */}
            <Route path="/contact" element={
                isNative ? <Navigate to="/login" replace /> : <ContactPage />
            } />
            
            {/* Public Privacy Policy Page */}
            <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
            
            <Route path="/login" element={
                isAuthenticated ? <Navigate to="/portal" replace /> : <LoginPage />
            } />
            
            {/* Member Hub - Main landing page after login */}
            <Route path="/portal" element={
                <ProtectedRoute>
                    <MemberHubPage />
                </ProtectedRoute>
            } />
            
            {/* Dashboard & Admin Pages - with Layout */}
            <Route path="/dashboard" element={
                <ProtectedRoute>
                    <Layout />
                </ProtectedRoute>
            }>
                <Route index element={<DashboardPage />} />
            </Route>
            
            {/* Other Pages with back navigation */}
            <Route path="/members" element={
                <ProtectedRoute>
                    <PortalMembersDirectory />
                </ProtectedRoute>
            } />
            <Route path="/members/:id" element={
                <ProtectedRoute>
                    <MemberProfilePage />
                </ProtectedRoute>
            } />
            
            {/* Admin Members Management */}
            <Route path="/admin/members" element={
                <ProtectedRoute>
                    <MembersPageNew />
                </ProtectedRoute>
            } />
            
            <Route path="/attendance" element={
                <ProtectedRoute>
                    <LogbookPage />
                </ProtectedRoute>
            } />
            
            <Route path="/logbook" element={
                <ProtectedRoute>
                    <LogbookPage />
                </ProtectedRoute>
            } />
            
            <Route path="/calendar" element={
                <ProtectedRoute>
                    <AppCalendarPage />
                </ProtectedRoute>
            } />
            
            {/* Admin Calendar Management */}
            <Route path="/admin/calendar" element={
                <ProtectedRoute>
                    <AdminCalendarPage />
                </ProtectedRoute>
            } />
            
            <Route path="/admin/activities" element={
                <ProtectedRoute>
                    <ManageActivitiesPage />
                </ProtectedRoute>
            } />
            
            <Route path="/admin/permissions" element={
                <ProtectedRoute>
                    <Layout />
                </ProtectedRoute>
            }>
                <Route index element={<PermissionEditorPage />} />
            </Route>
            
            <Route path="/admin/timecard" element={
                <ProtectedRoute>
                    <TimecardPage />
                </ProtectedRoute>
            } />
            
            <Route path="/admin/duty-officer-timecard" element={
                <ProtectedRoute>
                    <Layout />
                </ProtectedRoute>
            }>
                <Route index element={<DutyOfficerTimecardPage />} />
            </Route>
            
            <Route path="/admin/audit-logs" element={
                <ProtectedRoute>
                    <AuditLogPage />
                </ProtectedRoute>
            } />
            
            <Route path="/admin/security" element={
                <ProtectedRoute>
                    <SecurityDashboardPage />
                </ProtectedRoute>
            } />
            
            <Route path="/admin/contact-submissions" element={
                <ProtectedRoute>
                    <ContactSubmissionsPage />
                </ProtectedRoute>
            } />
            
            <Route path="/equipment" element={
                <ProtectedRoute>
                    <Layout />
                </ProtectedRoute>
            }>
                <Route index element={<EquipmentPage />} />
            </Route>
            
            <Route path="/vehicles" element={
                <ProtectedRoute>
                    <Layout />
                </ProtectedRoute>
            }>
                <Route index element={<VehiclesPage />} />
            </Route>
            
            <Route path="/forms" element={
                <ProtectedRoute>
                    <Layout />
                </ProtectedRoute>
            }>
                <Route index element={<FormsPage />} />
            </Route>
            
            <Route path="/settings" element={
                <ProtectedRoute>
                    <Layout />
                </ProtectedRoute>
            }>
                <Route index element={<SettingsPage />} />
            </Route>
            
            {/* New Pages */}
            <Route path="/resources" element={
                <ProtectedRoute>
                    <ResourcesPage />
                </ProtectedRoute>
            } />
            
            <Route path="/training" element={
                <ProtectedRoute>
                    <TrainingPage />
                </ProtectedRoute>
            } />
            
            <Route path="/links" element={
                <ProtectedRoute>
                    <LinksPage />
                </ProtectedRoute>
            } />
            
            <Route path="*" element={<Navigate to="/portal" replace />} />
        </Routes>
    );
};

function App() {
    // Initialize native app features on mount
    useEffect(() => {
        if (isNative) {
            initializeNativeApp();
            setupBackButtonHandler(() => {
                // Optional: Handle exit confirmation for Android
            });
        }
    }, []);

    return (
        <BrowserRouter>
            <AuthProvider>
                <AppRoutes />
                <Toaster position="top-right" richColors />
            </AuthProvider>
        </BrowserRouter>
    );
}

export default App;
